type FileType = {
    ext: string;
    mime: string;
} | null;

const toBytes = (s: string) => Array.from(s, c => c.codePointAt(0) as number);
const xpiZipFilename = toBytes('META-INF/mozilla.rsa');
const epubMimeSignature = toBytes('mimetypeapplication/epub+zip');
const oxmlContentTypes = toBytes('[Content_Types].xml');
const oxmlRels = toBytes('_rels/.rels');
const wordFolder = toBytes('word/');
const pptFolder = toBytes('ppt/');
const xlFolder = toBytes('xl/');

const check = (
    buf: Uint8Array,
    header: number[],
    opts: { offset?: number; mask?: number[] } = {}
) => {
    const { offset = 0, mask } = opts;

    for (let i = 0; i < header.length; i++) {
        if (mask) {
            if (header[i] !== (mask[i] & buf[i + offset])) {
                return false;
            }
        } else if (header[i] !== buf[i + offset]) {
            return false;
        }
    }

    return true;
};

const signatureTypes: Array<{
    header: number[];
    ext: string;
    mime: string;
    opts?: { offset?: number; mask?: number[] };
}> = [
    { header: [0xFF, 0xD8, 0xFF], ext: 'jpg', mime: 'image/jpeg' },
    { header: [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A], ext: 'png', mime: 'image/png' },
    { header: [0x47, 0x49, 0x46], ext: 'gif', mime: 'image/gif' },
    { header: [0x57, 0x45, 0x42, 0x50], opts: { offset: 8 }, ext: 'webp', mime: 'image/webp' },
    { header: [0x46, 0x4C, 0x49, 0x46], ext: 'flif', mime: 'image/flif' },
    { header: [0x42, 0x4D], ext: 'bmp', mime: 'image/bmp' },
    { header: [0x49, 0x49, 0xBC], ext: 'jxr', mime: 'image/vnd.ms-photo' },
    { header: [0x38, 0x42, 0x50, 0x53], ext: 'psd', mime: 'image/vnd.adobe.photoshop' },
    { header: [0x75, 0x73, 0x74, 0x61, 0x72], opts: { offset: 257 }, ext: 'tar', mime: 'application/x-tar' },
    { header: [0x30, 0x26, 0xB2, 0x75, 0x8E, 0x66, 0xCF, 0x11, 0xA6, 0xD9], ext: 'wmv', mime: 'video/x-ms-wmv' },
    { header: [0x0, 0x0, 0x1, 0xBA], ext: 'mpg', mime: 'video/mpeg' },
    { header: [0x4F, 0x67, 0x67, 0x53], ext: 'ogg', mime: 'audio/ogg' },
    { header: [0x66, 0x4C, 0x61, 0x43], ext: 'flac', mime: 'audio/x-flac' },
    { header: [0x23, 0x21, 0x41, 0x4D, 0x52, 0x0A], ext: 'amr', mime: 'audio/amr' },
    { header: [0x25, 0x50, 0x44, 0x46], ext: 'pdf', mime: 'application/pdf' },
    { header: [0x4D, 0x5A], ext: 'exe', mime: 'application/x-msdownload' },
    { header: [0x7B, 0x5C, 0x72, 0x74, 0x66], ext: 'rtf', mime: 'application/rtf' },
    { header: [0x00, 0x61, 0x73, 0x6D], ext: 'wasm', mime: 'application/wasm' },
    { header: [0x00, 0x01, 0x00, 0x00, 0x00], ext: 'ttf', mime: 'font/ttf' },
    { header: [0x4F, 0x54, 0x54, 0x4F, 0x00], ext: 'otf', mime: 'font/otf' },
    { header: [0x00, 0x00, 0x01, 0x00], ext: 'ico', mime: 'image/x-icon' },
    { header: [0x46, 0x4C, 0x56, 0x01], ext: 'flv', mime: 'video/x-flv' },
    { header: [0x25, 0x21], ext: 'ps', mime: 'application/postscript' },
    { header: [0xFD, 0x37, 0x7A, 0x58, 0x5A, 0x00], ext: 'xz', mime: 'application/x-xz' },
    { header: [0x53, 0x51, 0x4C, 0x69], ext: 'sqlite', mime: 'application/x-sqlite3' },
    { header: [0x4E, 0x45, 0x53, 0x1A], ext: 'nes', mime: 'application/x-nintendo-nes-rom' },
    { header: [0x43, 0x72, 0x32, 0x34], ext: 'crx', mime: 'application/x-google-chrome-extension' },
    { header: [0xED, 0xAB, 0xEE, 0xDB], ext: 'rpm', mime: 'application/x-rpm' },
    { header: [0x4C, 0x5A, 0x49, 0x50], ext: 'lz', mime: 'application/x-lzip' },
    { header: [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1], ext: 'msi', mime: 'application/x-msi' },
    { header: [0x06, 0x0E, 0x2B, 0x34, 0x02, 0x05, 0x01, 0x01, 0x0D, 0x01, 0x02, 0x01, 0x01, 0x02], ext: 'mxf', mime: 'application/mxf' },
    { header: [0x42, 0x4C, 0x45, 0x4E, 0x44, 0x45, 0x52], ext: 'blend', mime: 'application/x-blender' },
    { header: [0x42, 0x50, 0x47, 0xFB], ext: 'bpg', mime: 'image/bpg' },
    { header: [0x21, 0x3C, 0x61, 0x72, 0x63, 0x68, 0x3E, 0x0A, 0x64, 0x65, 0x62, 0x69, 0x61, 0x6E, 0x2D, 0x62, 0x69, 0x6E, 0x61, 0x72, 0x79], ext: 'deb', mime: 'application/x-deb' },
    { header: [0x21, 0x3C, 0x61, 0x72, 0x63, 0x68, 0x3E], ext: 'ar', mime: 'application/x-unix-archive' }
];

const detectStaticFileType = (buf: Uint8Array): FileType => {
    for (const signature of signatureTypes) {
        if (check(buf, signature.header, signature.opts || {})) {
            return { ext: signature.ext, mime: signature.mime };
        }
    }

    return null;
};

const detectCr2OrTiff = (buf: Uint8Array): FileType => {
    const isTiffHeader =
        check(buf, [0x49, 0x49, 0x2A, 0x0]) || check(buf, [0x4D, 0x4D, 0x0, 0x2A]);

    if (!isTiffHeader) {
        return null;
    }

    if (check(buf, [0x43, 0x52], { offset: 8 })) {
        return { ext: 'cr2', mime: 'image/x-canon-cr2' };
    }

    return { ext: 'tif', mime: 'image/tiff' };
};

const detectZipBasedFileType = (buf: Uint8Array): FileType => {
    if (!check(buf, [0x50, 0x4B, 0x03, 0x04])) {
        return null;
    }

    if (check(buf, epubMimeSignature, { offset: 30 })) {
        return { ext: 'epub', mime: 'application/epub+zip' };
    }

    if (check(buf, xpiZipFilename, { offset: 30 })) {
        return { ext: 'xpi', mime: 'application/x-xpinstall' };
    }

    const officeType = detectOfficeOpenXml(buf);
    if (officeType) {
        return officeType;
    }

    const versionByte = buf[2];
    const headerByte = buf[3];
    const validVersion = versionByte === 0x03 || versionByte === 0x05 || versionByte === 0x07;
    const validHeader = headerByte === 0x04 || headerByte === 0x06 || headerByte === 0x08;

    if (check(buf, [0x50, 0x4B]) && validVersion && validHeader) {
        return { ext: 'zip', mime: 'application/zip' };
    }

    return null;
};

const detectOfficeOpenXml = (buf: Uint8Array): FileType => {
    if (!check(buf, oxmlContentTypes, { offset: 30 }) && !check(buf, oxmlRels, { offset: 30 })) {
        return null;
    }

    const nextZipHeaderIndex = (arr: Uint8Array) =>
        arr.findIndex((el, i, arr2) =>
            el === 0x50 &&
            arr2[i + 1] === 0x4B &&
            arr2[i + 2] === 0x03 &&
            arr2[i + 3] === 0x04
        );

    const firstSlice = buf.subarray(4, 4 + 2000);
    const header2Pos = nextZipHeaderIndex(firstSlice);
    if (header2Pos === -1) {
        return null;
    }

    const secondSlice = buf.subarray(header2Pos + 8, header2Pos + 8 + 1000);
    const header3Pos = nextZipHeaderIndex(secondSlice);
    if (header3Pos === -1) {
        return null;
    }

    const offset = 8 + header2Pos + header3Pos + 30;
    if (check(buf, wordFolder, { offset })) {
        return { ext: 'docx', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' };
    }

    if (check(buf, pptFolder, { offset })) {
        return { ext: 'pptx', mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' };
    }

    if (check(buf, xlFolder, { offset })) {
        return { ext: 'xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' };
    }

    return null;
};

const detectMp4FileType = (buf: Uint8Array): FileType => {
    if (check(buf, [0x33, 0x67, 0x70, 0x35])) {
        return { ext: 'mp4', mime: 'video/mp4' };
    }

    if (!check(buf, [0x0, 0x0, 0x0]) || !check(buf, [0x66, 0x74, 0x79, 0x70], { offset: 4 })) {
        return null;
    }

    const mp4Subtypes = [
        [0x6D, 0x70, 0x34, 0x31],
        [0x6D, 0x70, 0x34, 0x32],
        [0x69, 0x73, 0x6F, 0x6D],
        [0x69, 0x73, 0x6F, 0x32],
        [0x6D, 0x6D, 0x70, 0x34],
        [0x4D, 0x34, 0x56],
        [0x64, 0x61, 0x73, 0x68]
    ];

    for (const subtype of mp4Subtypes) {
        if (check(buf, subtype, { offset: 8 })) {
            return { ext: 'mp4', mime: 'video/mp4' };
        }
    }

    return null;
};

const detectMatroskaFileType = (buf: Uint8Array): FileType => {
    if (!check(buf, [0x1A, 0x45, 0xDF, 0xA3])) {
        return null;
    }

    const sliced = buf.subarray(4, 4 + 4096);
    const idPos = sliced.findIndex((el, i, arr) => arr[i] === 0x42 && arr[i + 1] === 0x82);
    if (idPos === -1) {
        return null;
    }

    const docTypePos = idPos + 3;
    const findDocType = (value: string) =>
        Array.from(value).every((c, i) => sliced[docTypePos + i] === c.codePointAt(0));

    if (findDocType('matroska')) {
        return { ext: 'mkv', mime: 'video/x-matroska' };
    }

    if (findDocType('webm')) {
        return { ext: 'webm', mime: 'video/webm' };
    }

    return null;
};

const detectMovFileType = (buf: Uint8Array): FileType => {
    const movHeaders = [
        [0x0, 0x0, 0x0, 0x14, 0x66, 0x74, 0x79, 0x70, 0x71, 0x74, 0x20, 0x20],
        [0x66, 0x72, 0x65, 0x65],
        [0x66, 0x74, 0x79, 0x70, 0x71, 0x74, 0x20, 0x20],
        [0x6D, 0x64, 0x61, 0x74],
        [0x77, 0x69, 0x64, 0x65]
    ];

    for (const header of movHeaders) {
        if (check(buf, header, { offset: 4 })) {
            return { ext: 'mov', mime: 'video/quicktime' };
        }
    }

    return null;
};

const detectAviFileType = (buf: Uint8Array): FileType => {
    if (check(buf, [0x52, 0x49, 0x46, 0x46]) && check(buf, [0x41, 0x56, 0x49], { offset: 8 })) {
        return { ext: 'avi', mime: 'video/x-msvideo' };
    }

    return null;
};

const detectMp3FileType = (buf: Uint8Array): FileType => {
    for (let start = 0; start < 2 && start < buf.length - 16; start++) {
        if (
            check(buf, [0x49, 0x44, 0x33], { offset: start }) ||
            check(buf, [0xFF, 0xE2], { offset: start, mask: [0xFF, 0xE2] })
        ) {
            return { ext: 'mp3', mime: 'audio/mpeg' };
        }
    }

    return null;
};

const detectM4aFileType = (buf: Uint8Array): FileType => {
    if (
        check(buf, [0x66, 0x74, 0x79, 0x70, 0x4D, 0x34, 0x41], { offset: 4 }) ||
        check(buf, [0x4D, 0x34, 0x41, 0x20])
    ) {
        return { ext: 'm4a', mime: 'audio/m4a' };
    }

    return null;
};

const detectOpusFileType = (buf: Uint8Array): FileType => {
    if (check(buf, [0x4F, 0x70, 0x75, 0x73, 0x48, 0x65, 0x61, 0x64], { offset: 28 })) {
        return { ext: 'opus', mime: 'audio/opus' };
    }

    return null;
};

const detectSwfFileType = (buf: Uint8Array): FileType => {
    if ((buf[0] === 0x43 || buf[0] === 0x46) && check(buf, [0x57, 0x53], { offset: 1 })) {
        return { ext: 'swf', mime: 'application/x-shockwave-flash' };
    }

    return null;
};

const detectWoffFileType = (buf: Uint8Array): FileType => {
    if (
        check(buf, [0x77, 0x4F, 0x46, 0x46]) &&
        (check(buf, [0x00, 0x01, 0x00, 0x00], { offset: 4 }) || check(buf, [0x4F, 0x54, 0x54, 0x4F], { offset: 4 }))
    ) {
        return { ext: 'woff', mime: 'font/woff' };
    }

    return null;
};

const detectWoff2FileType = (buf: Uint8Array): FileType => {
    if (
        check(buf, [0x77, 0x4F, 0x46, 0x32]) &&
        (check(buf, [0x00, 0x01, 0x00, 0x00], { offset: 4 }) || check(buf, [0x4F, 0x54, 0x54, 0x4F], { offset: 4 }))
    ) {
        return { ext: 'woff2', mime: 'font/woff2' };
    }

    return null;
};

const detectEotFileType = (buf: Uint8Array): FileType => {
    if (
        check(buf, [0x4C, 0x50], { offset: 34 }) &&
        (check(buf, [0x00, 0x00, 0x01], { offset: 8 }) || check(buf, [0x01, 0x00, 0x02], { offset: 8 }) || check(buf, [0x02, 0x00, 0x02], { offset: 8 }))
    ) {
        return { ext: 'eot', mime: 'application/octet-stream' };
    }

    return null;
};

const detectMtsFileType = (buf: Uint8Array): FileType => {
    if (check(buf, [0x47], { offset: 4 }) && (check(buf, [0x47], { offset: 192 }) || check(buf, [0x47], { offset: 196 }))) {
        return { ext: 'mts', mime: 'video/mp2t' };
    }

    return null;
};

const detectFileType = (buf: Uint8Array): FileType => {
    const detectors = [
        detectCr2OrTiff,
        detectZipBasedFileType,
        detectMp4FileType,
        detectMatroskaFileType,
        detectMovFileType,
        detectAviFileType,
        detectMp3FileType,
        detectM4aFileType,
        detectOpusFileType,
        detectSwfFileType,
        detectWoffFileType,
        detectWoff2FileType,
        detectEotFileType,
        detectMtsFileType
    ];

    for (const detector of detectors) {
        const result = detector(buf);
        if (result) {
            return result;
        }
    }

    return detectStaticFileType(buf);
};

export default (buf: Uint8Array) => {
    if (!buf || buf.length < 2) {
        return null;
    }

    return detectFileType(buf);
};
